// ── Visual-viewport bottom pinning ────────────────────────────────
//
// `position: fixed; bottom: 0` pins an element to the **layout** viewport.
// On mobile that is often NOT the area the user can actually see:
//
//   • iOS Safari's URL bar and toolbar collapse and expand as you scroll.
//     The layout viewport stays full-height throughout, so a bottom-fixed
//     bar sits *below* the visible region and appears to drift or detach
//     while scrolling, snapping back only once the scroll settles.
//   • When the on-screen keyboard opens, the visual viewport shrinks but
//     the layout viewport does not, so bottom-fixed UI hides behind it.
//
// The Visual Viewport API reports the region that is genuinely on screen.
// Measuring the gap between the two viewports and translating the element
// up by that much keeps it welded to the bottom of what the user sees.
//
// Returns 0 on desktop and anywhere the API is missing, so the plain
// `bottom: 0` behaviour is preserved and nothing regresses.

import { useState, useEffect } from 'react';

/**
 * Pixels between the bottom of the visual viewport and the bottom of the
 * layout viewport. Translate a bottom-fixed element up by this to keep it
 * pinned to the visible edge.
 */
export function useViewportBottom(): number {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return; // No API (older/desktop) — `bottom: 0` is already right.

    let frame = 0;

    const measure = () => {
      frame = 0;
      // Bottom edge of the visual viewport, in layout-viewport coordinates.
      const visibleBottom = vv.offsetTop + vv.height;
      const layoutHeight = document.documentElement.clientHeight;
      const gap = layoutHeight - visibleBottom;
      // Sub-pixel jitter would cause a repaint on every scroll frame, and
      // a negative gap (over-scroll / rubber-banding) must not push the
      // bar off-screen downwards.
      const next = gap > 1 ? Math.round(gap) : 0;
      setOffset(prev => (prev === next ? prev : next));
    };

    // These fire continuously during an iOS scroll, so coalesce to one
    // measurement per frame.
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    };

    measure();
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    window.addEventListener('orientationchange', schedule);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      window.removeEventListener('orientationchange', schedule);
    };
  }, []);

  return offset;
}
